/**
 * Iranian mobile phone validation and formatting.
 *
 * Iranian mobile numbers start with 09 (national) or +989 (international).
 * All have 11 digits after the leading 0 (or 10 after +98).
 */

import { parsePhoneNumberFromString, isValidPhoneNumber } from 'libphonenumber-js';

/**
 * Check if a string is a valid Iranian mobile number.
 *
 * @param {string} phone - User input (any format)
 * @returns {boolean}
 *
 * @example
 *   isValidIranianMobile('+989123456789')  // true
 *   isValidIranianMobile('09123456789')    // true
 *   isValidIranianMobile('912 345 6789')   // true
 *   isValidIranianMobile('12345')          // false
 */
export function isValidIranianMobile(phone) {
  if (!phone) return false;
  try {
    return isValidPhoneNumber(phone, 'IR');
  } catch {
    return false;
  }
}

/**
 * Convert any valid Iranian mobile to E.164 format (the canonical form
 * used everywhere in the backend).
 *
 * @param {string} phone
 * @returns {string|null} E.164 format like "+989123456789", or null if invalid
 *
 * @example
 *   toE164('09123456789')   // "+989123456789"
 *   toE164('912 345 6789')  // "+989123456789"
 *   toE164('invalid')       // null
 */
export function toE164(phone) {
  if (!phone) return null;
  try {
    const parsed = parsePhoneNumberFromString(phone, 'IR');
    if (!parsed || !parsed.isValid()) return null;
    return parsed.format('E.164');
  } catch {
    return null;
  }
}

/**
 * Format a phone number for display (national format with spaces).
 *
 * @param {string} phone
 * @returns {string} formatted display string, or original if can't parse
 *
 * @example
 *   formatPhoneDisplay('+989123456789')  // "0912 345 6789"
 */
export function formatPhoneDisplay(phone) {
  if (!phone) return '';
  try {
    const parsed = parsePhoneNumberFromString(phone, 'IR');
    if (!parsed) return phone;
    return parsed.formatNational();
  } catch {
    return phone;
  }
}
